import { useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { AlertCircle, RotateCw } from "lucide-react";
import Gallery from "../../../components/Gallery/Gallery";
import ModalMedia from "../../../components/Modal/ModalMedia";
import ButtonAction from "../../../components/Buttons/Button";
import { ConfirmAlert, SimpleAlert } from "../../../components/Alerts/Alerts";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { startAddContentsToList } from "../../../actions/user";
import { getPathDatabaseByRole } from "../../../actions/auth";
import { useTemplatesModals } from "../../../contexts/templatesModalsContext";
import { useContentModals } from "../../../contexts/contentModalsContext";
import {
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  ALERT_COLORS,
  BORDER_RADIUS,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "../../../utils/constants";
import {
  BUTTON_DESELECT_ALL,
  BUTTON_RETRY,
  BUTTON_SELECT_ALL,
  MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY,
  MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY_NO_COUNT,
  MODAL_CREATE_WITH_IA_SAVED_SUCCESSFULLY,
  MODAL_CREATE_WITH_IA_SAVING_TO_GALLERY,
  TEMPLATES_PRODUCE_FAILURE_GENERIC,
  TEMPLATES_PRODUCE_GENERATING,
  TEMPLATES_PRODUCE_PARTIAL_TITLE,
  TEMPLATES_PRODUCE_PARTIAL_SUBTITLE_SINGULAR,
  TEMPLATES_PRODUCE_PARTIAL_SUBTITLE_PLURAL,
  TEMPLATES_PRODUCE_RESULTS,
  TEMPLATES_PRODUCE_SAVE_ERROR,
  TEMPLATES_PRODUCE_DELETE_CONFIRM_TEXT,
  TEMPLATES_PRODUCE_DELETE_CONFIRM_TITLE,
  TEMPLATES_PRODUCE_DELETE_ERROR,
  TEMPLATES_PRODUCE_DELETE_SELECTED,
  TEMPLATES_PRODUCE_RUN_LEGACY,
  TEMPLATES_PRODUCE_RUN_TITLE,
} from "../../../i18n/keysTranslations";
import { groupProduceItemsByRun } from "./produceRunGroups";

const WARNING_ALERT_COLORS = ALERT_COLORS.yellow;

const loadingBoxSx = {
  aspectRatio: "1",
  bgcolor: "grey.100",
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: 1,
  width: "100%",
  minHeight: 180,
};

const runTitleSx = { fontWeight: 600, fontSize: "1rem" };

const getItemKey = (item) => String(item?.id || item?.url || "");

const getGallerySortKey = (item) => ({
  row: Number(item?.sortRow ?? item?.rowIndex ?? item?.productionRowIndex ?? 0),
  slide: Number(item?.sortSlide ?? item?.slideIndex ?? 0),
  artboard: String(item?.sortArtboard ?? item?.artboardID ?? ""),
});

const mergeProduceGalleryItems = ({
  items = [],
  failedItems = [],
  isGenerating,
}) => {
  const successItems = items.map((item) => ({
    ...item,
    sortRow: item.rowIndex ?? 0,
    sortSlide: item.slideIndex ?? 0,
    sortArtboard: item.artboardID || "",
  }));
  const failed = isGenerating
    ? []
    : failedItems.map((item) => ({
        id: item.id,
        jobID: item.jobID || null,
        artboardID: item.artboardID || null,
        isFailed: true,
        sortRow: item.productionRowIndex ?? 0,
        sortSlide: item.slideIndex ?? 0,
        sortArtboard: item.artboardID || "",
      }));

  return [...successItems, ...failed].sort((left, right) => {
    const leftKey = getGallerySortKey(left);
    const rightKey = getGallerySortKey(right);
    if (leftKey.row !== rightKey.row) return leftKey.row - rightKey.row;
    if (leftKey.slide !== rightKey.slide) return leftKey.slide - rightKey.slide;
    return leftKey.artboard.localeCompare(rightKey.artboard);
  });
};

const ProduceFailureTile = () => {
  const { t } = useTranslationApp();
  return (
    <Box sx={loadingBoxSx}>
      <AlertCircle size={28} color="#d32f2f" />
      <Typography
        variant="body2"
        sx={{ textAlign: "center", px: 1, color: "error.main" }}
      >
        {t(TEMPLATES_PRODUCE_FAILURE_GENERIC)}
      </Typography>
    </Box>
  );
};

const ProduceResultsGallery = ({
  items = [],
  failedItems = [],
  summary = null,
  isGenerating = false,
  pendingCount = 0,
  columns = { xs: 1, sm: 2, md: 3, lg: 4 },
  onEditItem = () => {},
  onRetryFailed = null,
  isRetrying = false,
  runs = {},
  onDeleteItems = null,
}) => {
  const { t, i18n } = useTranslationApp();
  const [isDeleting, setIsDeleting] = useState(false);
  // Fixed at the moment a generation starts so the title does not tick.
  const generationStartedAt = useMemo(
    () => (isGenerating ? Date.now() : 0),
    [isGenerating]
  );
  const dispatch = useDispatchApp();
  const { closeTemplatesModals } = useTemplatesModals();
  const { openGallery } = useContentModals();
  const [preview, setPreview] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const galleryData = useMemo(
    () => mergeProduceGalleryItems({ items, failedItems, isGenerating }),
    [failedItems, isGenerating, items]
  );
  const loadingItems = isGenerating ? Math.max(pendingCount, 0) : 0;
  const hasSelectableItems = items.length > 0;
  const loadingLabel = t(TEMPLATES_PRODUCE_GENERATING);

  const selectableKeys = useMemo(
    () => items.map(getItemKey).filter(Boolean),
    [items]
  );
  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const runGroups = useMemo(
    () => groupProduceItemsByRun({ items: galleryData, runs }),
    [galleryData, runs]
  );
  const getSelectedIndices = useCallback(
    (sectionItems) =>
      sectionItems.reduce((acc, item, index) => {
        if (item.isFailed) return acc;
        const key = getItemKey(item);
        if (key && selectedKeySet.has(key)) acc.push(index);
        return acc;
      }, []),
    [selectedKeySet]
  );
  const selectedCount = useMemo(
    () => items.filter((item) => selectedKeySet.has(getItemKey(item))).length,
    [items, selectedKeySet]
  );
  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => selectedKeySet.has(key));

  const onSelectPreview = useCallback((sectionItems, indexOrData) => {
    const isObject = typeof indexOrData === "object" && indexOrData !== null;
    const index = isObject ? indexOrData.index : indexOrData;
    const item = sectionItems[index];
    if (item?.isFailed) return;
    const isCarouselPreview = Boolean(
      isObject ? indexOrData.isCarousel : item?.isCarousel
    );
    if (!item?.url && !(isCarouselPreview && item?.carouselImages?.length)) {
      return;
    }
    setPreview({
      item,
      carouselIndex: isObject ? indexOrData.carouselIndex : 0,
      isCarousel: isCarouselPreview,
      isFromCarousel: Boolean(isObject && indexOrData.isFromCarousel),
    });
  }, []);

  const onClosePreview = useCallback(() => {
    setPreview(null);
  }, []);

  const previewItem = useMemo(() => {
    if (!preview?.item) return null;
    if (preview.isCarousel && preview.item.carouselImages) {
      return {
        ...preview.item,
        _initialCarouselIndex: preview.carouselIndex ?? 0,
      };
    }
    return preview.item;
  }, [preview]);

  const onSelectItem = useCallback((sectionItems, index) => {
    const item = sectionItems[index];
    if (item?.isFailed) return;
    const key = getItemKey(item);
    if (!key) return;
    setSelectedKeys((prev) =>
      prev.includes(key)
        ? prev.filter((itemKey) => itemKey !== key)
        : [...prev, key]
    );
  }, []);

  const deleteWithConfirmation = useCallback(
    async (itemsToDelete) => {
      if (!onDeleteItems || itemsToDelete.length === 0 || isDeleting) return;
      const confirmed = await ConfirmAlert({
        title: t(TEMPLATES_PRODUCE_DELETE_CONFIRM_TITLE, {
          count: itemsToDelete.length,
        }),
        text: t(TEMPLATES_PRODUCE_DELETE_CONFIRM_TEXT),
      });
      if (!confirmed.isConfirmed) return;
      setIsDeleting(true);
      try {
        const result = await onDeleteItems({ items: itemsToDelete });
        if (result?.ok) {
          const deletedKeys = new Set(itemsToDelete.map(getItemKey));
          setSelectedKeys((prev) =>
            prev.filter((key) => !deletedKeys.has(key))
          );
          return;
        }
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_DELETE_ERROR),
          icon: "error",
        });
      } finally {
        setIsDeleting(false);
      }
    },
    [isDeleting, onDeleteItems, t]
  );

  const onDeleteSelected = useCallback(
    () =>
      deleteWithConfirmation(
        items.filter((item) => selectedKeySet.has(getItemKey(item)))
      ),
    [deleteWithConfirmation, items, selectedKeySet]
  );

  const onDeleteSingle = useCallback(
    ({ item } = {}) => {
      if (item && !item.isFailed) deleteWithConfirmation([item]);
    },
    [deleteWithConfirmation]
  );

  const formatRunTitle = (group) => {
    if (group.isLegacy || !group.timestamp) {
      return t(TEMPLATES_PRODUCE_RUN_LEGACY);
    }
    return t(TEMPLATES_PRODUCE_RUN_TITLE, {
      date: new Date(group.timestamp).toLocaleString(
        i18n.language || undefined,
        { dateStyle: "medium", timeStyle: "short" }
      ),
    });
  };

  const onSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedKeys([]);
      return;
    }
    setSelectedKeys(selectableKeys);
  }, [allSelected, selectableKeys]);

  const onSaveToGallery = useCallback(async () => {
    if (selectedCount === 0 || isSaving) return;
    const itemsToSave = items
      .filter((item) => selectedKeySet.has(getItemKey(item)) && item.url)
      .map((item) => {
        if (item.isCarousel && Array.isArray(item.carouselImages)) {
          return {
            url: item.url,
            type: item.type || "upload",
            isAIGenerated: true,
            isCarousel: true,
            carouselImages: item.carouselImages.map((image) => ({
              url: image.url,
              id: image.id,
            })),
          };
        }
        return {
          url: item.url,
          type: item.type || "upload",
          isAIGenerated: true,
        };
      });
    if (itemsToSave.length === 0) return;
    setIsSaving(true);
    try {
      const saveResult = await dispatch(
        startAddContentsToList(itemsToSave, { preserveOrder: true })
      );
      if (!saveResult) {
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_SAVE_ERROR),
          icon: "error",
        });
        return;
      }
      setSelectedKeys([]);
      SimpleAlert({
        title: t(MODAL_CREATE_WITH_IA_SAVED_SUCCESSFULLY),
        icon: "success",
      });
      closeTemplatesModals();
      const { userID } = dispatch(getPathDatabaseByRole());
      openGallery({
        replaceExisting: true,
        multiple: true,
        isOutsideCampaign: true,
        acceptedFormats: IMAGE_FORMATS.concat(VIDEO_FORMATS).concat(
          ADS_CAMPAIGN_CAROUSEL_FORMAT
        ),
        pathStorage: `socialMedia/${userID}/`,
        modeUpload: "sync",
        onCallbackSelectedItems: () => {},
        closeActionsDisabled: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    closeTemplatesModals,
    dispatch,
    isSaving,
    items,
    openGallery,
    selectedCount,
    selectedKeySet,
    t,
  ]);

  const failedCount = failedItems.length || Number(summary?.failed) || 0;
  const succeededCount = Number(summary?.succeeded) || items.length;
  const requestedCount =
    Number(summary?.requested) || items.length + failedCount;
  const showPartialBanner =
    !isGenerating && failedCount > 0 && succeededCount > 0 && summary;
  if (!isGenerating && galleryData.length === 0) return null;

  const renderGallery = ({
    data,
    selectedItems,
    isLoading = false,
    loadingItems: loadingCount = 0,
    onSelectPreview: onSelectPreviewItem = () => {},
    onSelectItem: onSelectGalleryItem = () => {},
  }) => (
    <Gallery
      data={data}
      columns={columns}
      showEmptyMessage={false}
      showDeleteButton={false}
      showPostButton={false}
      showActionButtons={true}
      showResizeFormatButton={false}
      showEditButton={false}
      showDimensionsOverlay={true}
      showPreviewExpandOnly={false}
      showExpandButton={true}
      showFooterEditButton={true}
      onFooterEdit={onEditItem}
      showFooterDeleteButton={Boolean(onDeleteItems)}
      onFooterDelete={onDeleteSingle}
      onSelectPreview={onSelectPreviewItem}
      onSelectItem={onSelectGalleryItem}
      selectedItems={selectedItems}
      multiple={true}
      acceptedFormats={IMAGE_FORMATS.concat(ADS_CAMPAIGN_CAROUSEL_FORMAT)}
      loadLastItem={true}
      isLoading={isLoading}
      loadingItems={loadingCount}
      sxGallery={{ pl: 0, pr: 0 }}
      loadingComponent={{
        component: (
          <Box sx={loadingBoxSx}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              {loadingLabel}
            </Typography>
          </Box>
        ),
      }}
      itemFailedComponent={{
        component: ProduceFailureTile,
      }}
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#fff",
        border: "1px solid #E4E4E7",
        borderRadius: "14px",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
            {t(TEMPLATES_PRODUCE_RESULTS)}
          </Typography>
        </Box>
        {hasSelectableItems ? (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            <ButtonAction
              color="secondary"
              type="button"
              onClick={onSelectAll}
              disabled={isGenerating || !hasSelectableItems}
            >
              {allSelected ? t(BUTTON_DESELECT_ALL) : t(BUTTON_SELECT_ALL)}
            </ButtonAction>
            {onDeleteItems ? (
              <ButtonAction
                color="secondary"
                type="button"
                loading={isDeleting}
                disabled={isDeleting || isGenerating || selectedCount === 0}
                onClick={onDeleteSelected}
              >
                {t(TEMPLATES_PRODUCE_DELETE_SELECTED, { count: selectedCount })}
              </ButtonAction>
            ) : null}
            <ButtonAction
              color="primary"
              type="button"
              loading={isSaving}
              disabled={isSaving || selectedCount === 0}
              onClick={onSaveToGallery}
            >
              {isSaving
                ? t(MODAL_CREATE_WITH_IA_SAVING_TO_GALLERY)
                : selectedCount === 0
                  ? t(MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY_NO_COUNT)
                  : t(MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY, {
                      count: selectedCount,
                    })}
            </ButtonAction>
          </Box>
        ) : null}
      </Box>
      {showPartialBanner ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
            backgroundColor: WARNING_ALERT_COLORS.background,
            color: WARNING_ALERT_COLORS.color,
            border: `1px solid ${WARNING_ALERT_COLORS.border}`,
            borderRadius: BORDER_RADIUS,
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            px: 2,
            py: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              flex: 1,
            }}
          >
            <ErrorOutlineOutlinedIcon
              sx={{
                flexShrink: 0,
                color: `${WARNING_ALERT_COLORS.color} !important`,
                fontSize: "1.3rem",
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: `${WARNING_ALERT_COLORS.color} !important`,
                }}
              >
                {t(TEMPLATES_PRODUCE_PARTIAL_TITLE, {
                  succeeded: succeededCount,
                  requested: requestedCount,
                })}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.25,
                  color: `${WARNING_ALERT_COLORS.color} !important`,
                }}
              >
                {failedCount === 1
                  ? t(TEMPLATES_PRODUCE_PARTIAL_SUBTITLE_SINGULAR)
                  : t(TEMPLATES_PRODUCE_PARTIAL_SUBTITLE_PLURAL, {
                      failed: failedCount,
                    })}
              </Typography>
            </Box>
          </Box>
          {onRetryFailed ? (
            <Button
              variant="text"
              type="button"
              disabled={isRetrying}
              onClick={onRetryFailed}
              startIcon={
                isRetrying ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RotateCw size={16} />
                )
              }
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                color: `${WARNING_ALERT_COLORS.color} !important`,
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.06)",
                },
              }}
            >
              {t(BUTTON_RETRY)}
            </Button>
          ) : null}
        </Box>
      ) : null}
      {isGenerating && loadingItems > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={runTitleSx}>
            {formatRunTitle({ timestamp: generationStartedAt })}
          </Typography>
          {renderGallery({
            data: [],
            selectedItems: [],
            isLoading: true,
            loadingItems,
          })}
        </Box>
      ) : null}
      {runGroups.map((group) => (
        <Box
          key={group.key}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Typography sx={runTitleSx}>{formatRunTitle(group)}</Typography>
          {group.sections.map((section) => (
            <Box
              key={`${group.key}-${section.key}`}
              sx={{ display: "flex", flexDirection: "column", gap: 1 }}
            >
              {group.sections.length > 1 && section.width > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {`${section.width} × ${section.height}`}
                </Typography>
              ) : null}
              {renderGallery({
                data: section.items,
                selectedItems: getSelectedIndices(section.items),
                onSelectPreview: (indexOrData) =>
                  onSelectPreview(section.items, indexOrData),
                onSelectItem: (index) => onSelectItem(section.items, index),
              })}
            </Box>
          ))}
        </Box>
      ))}
      {previewItem ? (
        <ModalMedia
          modalOpen={Boolean(previewItem)}
          onCloseModal={onClosePreview}
          url={previewItem.url}
          item={previewItem}
          isFromCarousel={Boolean(preview?.isFromCarousel)}
          showEditButton={false}
          showShareButton={false}
          showDeleteButton={false}
          isPicker={true}
        />
      ) : null}
    </Box>
  );
};

export default ProduceResultsGallery;
